import React, { useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { http, BACKEND_ORIGIN } from "@/lib/api";
import { Label } from "@/components/ui/label";

// Resolve a stored value ("/api/files/{id}" or a legacy absolute URL) to a renderable src.
export const resolveAsset = (val) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  if (val.startsWith("/api/")) return `${BACKEND_ORIGIN}${val}`;
  return val;
};

// Single source of truth for image upload guidance per kind.
const SPEC = {
  logo: { rec: "400×120", min: "200×60", fmts: "PNG, JPG, WEBP or SVG", max: 2 },
  favicon: { rec: "64×64", min: "32×32", fmts: "PNG or SVG", max: 2 },
  avatar: { rec: "256×256 (square)", min: "128×128", fmts: "PNG, JPG or WEBP", max: 2 },
  product: { rec: "1200×630", min: "600×315", fmts: "PNG, JPG, WEBP or SVG", max: 5 },
  login_bg: { rec: "1200×1600", min: "800×1000", fmts: "PNG, JPG or WEBP", max: 5 },
  cover: { rec: "1200×630", min: "600×315", fmts: "PNG, JPG or WEBP", max: 5 },
  other: { rec: "1200×630", min: "600×315", fmts: "PNG, JPG, WEBP or SVG", max: 5 },
};

const specFor = (kind) => SPEC[kind] || SPEC.other;
const maxMbFor = (kind) => specFor(kind).max;

export function FileUpload({ label, value, onChange, kind = "other", hint, testId, aspect = "wide" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = resolveAsset(value);
  const spec = specFor(kind);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okTypes = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
    if (!okTypes.includes(file.type)) return toast.error("Use PNG, JPG, WEBP or SVG");
    const maxMb = maxMbFor(kind);
    if (file.size > maxMb * 1024 * 1024) return toast.error(`File too large (max ${maxMb}MB)`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/uploads", fd, { params: { kind }, headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const box = aspect === "favicon" ? "h-16 w-16" : aspect === "logo" ? "h-16 w-40" : "h-24 w-full";

  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs font-medium text-muted-foreground">{label}</Label>}
      <div className={`relative flex ${box} items-center justify-center overflow-hidden rounded-lg border border-dashed border-white/[0.15] bg-[#0A0A0A]`} data-testid={testId}>
        {src ? (
          <img src={src} alt="" loading="lazy" decoding="async" className="h-full w-full object-contain" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <ImageIcon className="h-5 w-5" strokeWidth={1.5} />
            <span className="text-[11px]">No image</span>
          </div>
        )}
        {busy && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={pick} disabled={busy} data-testid={testId ? `${testId}-btn` : undefined}
          className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.12] bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground transition-colors hover:bg-white/[0.06] disabled:opacity-50">
          <Upload className="h-3.5 w-3.5" /> Upload
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:text-[#EF4444]">
            <X className="h-3.5 w-3.5" /> Remove
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground/70" data-testid={testId ? `${testId}-spec` : undefined}>
        Recommended {spec.rec} · Min {spec.min} · {spec.fmts} · Max {spec.max}MB
      </p>
      {hint && <p className="text-xs text-muted-foreground/60">{hint}</p>}
      <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={onFile} />
    </div>
  );
}
